/**
 * Error Logger
 * 
 * Sistema global de captura de erros para diagnóstico em produção
 */

interface GlobalError {
  type: 'error' | 'unhandledrejection' | 'resource';
  timestamp: string;
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  url?: string;
  userAgent: string;
  environment: string;
}

/**
 * Inicializar sistema de log de erros
 */
export function initErrorLogger(): void {
  // Já inicializado
  if ((window as any).__errorLoggerInitialized) {
    return;
  }

  console.log('[ErrorLogger] ========================================');
  console.log('[ErrorLogger] Sistema de captura de erros ATIVADO');
  console.log('[ErrorLogger] Ambiente:', import.meta.env.MODE);
  console.log('[ErrorLogger] URL:', window.location.href);
  console.log('[ErrorLogger] ========================================');
  
  // Capturar erros globais do JavaScript
  window.addEventListener('error', (event) => {
    console.error('[GLOBAL ERROR]', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      stack: event.error?.stack,
    });

    const errorLog: GlobalError = {
      type: 'error',
      timestamp: new Date().toISOString(),
      message: event.message,
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      url: window.location.href,
      userAgent: navigator.userAgent,
      environment: import.meta.env.MODE,
    };

    saveError(errorLog);
  });

  // Capturar promises rejeitadas não tratadas
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[UNHANDLED REJECTION]', {
      reason: event.reason,
      promise: event.promise,
    });

    const errorLog: GlobalError = {
      type: 'unhandledrejection',
      timestamp: new Date().toISOString(),
      message: event.reason?.message || String(event.reason),
      stack: event.reason?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      environment: import.meta.env.MODE,
    };

    saveError(errorLog);
  });

  // Capturar erros de carregamento de recursos
  window.addEventListener('error', (event) => {
    if (event.target && event.target !== window) {
      const target = event.target as HTMLElement;
      console.error('[RESOURCE ERROR]', {
        tagName: target.tagName,
        src: (target as any).src || (target as any).href,
        type: event.type,
      });

      const errorLog: GlobalError = {
        type: 'resource',
        timestamp: new Date().toISOString(),
        message: `Failed to load resource: ${(target as any).src || (target as any).href}`,
        url: window.location.href,
        userAgent: navigator.userAgent,
        environment: import.meta.env.MODE,
      };

      saveError(errorLog);
    }
  }, true); // Use capture phase

  // Log de informações do ambiente ao iniciar
  console.log('[ErrorLogger] Ambiente:', {
    mode: import.meta.env.MODE,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  });

  (window as any).__errorLoggerInitialized = true;
}

/**
 * Salvar erro no localStorage
 */
function saveError(error: GlobalError): void {
  try {
    const existingErrors = JSON.parse(localStorage.getItem('global_errors') || '[]');
    existingErrors.push(error);
    
    // Manter apenas os últimos 20 erros
    if (existingErrors.length > 20) {
      existingErrors.shift();
    }
    
    localStorage.setItem('global_errors', JSON.stringify(existingErrors));
    
    // Também exibir no console com destaque
    console.group(`🔴 ${error.type.toUpperCase()} - ${new Date(error.timestamp).toLocaleTimeString()}`);
    console.error('Message:', error.message);
    if (error.stack) {
      console.error('Stack:', error.stack);
    }
    if (error.filename) {
      console.error('File:', error.filename, `(${error.lineno}:${error.colno})`);
    }
    console.error('URL:', error.url);
    console.groupEnd();
    
  } catch (e) {
    console.error('[ErrorLogger] Failed to save error:', e);
  }
}

/**
 * Obter todos os erros salvos
 */
export function getStoredErrors(): GlobalError[] {
  try {
    return JSON.parse(localStorage.getItem('global_errors') || '[]');
  } catch (e) {
    console.error('[ErrorLogger] Failed to get stored errors:', e);
    return [];
  }
}

/**
 * Limpar erros salvos
 */
export function clearStoredErrors(): void {
  try {
    localStorage.removeItem('global_errors');
    localStorage.removeItem('app_errors'); // Também limpar erros do ErrorBoundary
    console.log('[ErrorLogger] Erros limpos com sucesso');
  } catch (e) {
    console.error('[ErrorLogger] Failed to clear errors:', e);
  }
}

/**
 * Exportar erros para análise
 */
export function exportErrors(): string {
  const appErrors = JSON.parse(localStorage.getItem('app_errors') || '[]');
  const globalErrors = getStoredErrors();
  
  return JSON.stringify({
    timestamp: new Date().toISOString(),
    environment: {
      mode: import.meta.env.MODE,
      url: window.location.href,
      userAgent: navigator.userAgent,
    },
    appErrors,
    globalErrors,
  }, null, 2);
}

